import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  BetaRequest,
  BetaRequestsFilter,
  BetaRequestsResponse,
  BetaPendingCountResponse,
  ApproveBetaRequestPayload,
  RejectBetaRequestPayload,
} from '@/types'
import { toast } from 'sonner'

const BETA_QUERY_KEY = 'beta-requests'

export const useBetaRequests = (filters: BetaRequestsFilter = {}) => {
  return useQuery<BetaRequestsResponse>({
    queryKey: [BETA_QUERY_KEY, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })

      const { data } = await api.get(`/beta-requests?${params.toString()}`)
      return data
    },
  })
}

export const useBetaRequest = (id: string) => {
  return useQuery<BetaRequest>({
    queryKey: [BETA_QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await api.get(`/beta-requests/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export const useBetaPendingCount = () => {
  return useQuery<BetaPendingCountResponse>({
    queryKey: [BETA_QUERY_KEY, 'pending-count'],
    queryFn: async () => {
      const { data } = await api.get('/beta-requests/count')
      return data
    },
    refetchInterval: 60_000,
  })
}

export const useApproveBetaRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ApproveBetaRequestPayload }) => {
      const { data } = await api.post(`/beta-requests/${id}/approve`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BETA_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('Solicitud aprobada. Se creo el usuario y se envio email.')
    },
  })
}

export const useRejectBetaRequest = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: RejectBetaRequestPayload }) => {
      const { data } = await api.post(`/beta-requests/${id}/reject`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BETA_QUERY_KEY] })
      toast.success('Solicitud rechazada. Se envio email al solicitante.')
    },
  })
}
